/**
 * Shared tax-year resolution + per-year IRIS schema versioning for every A2A form.
 *
 * Promoted from 1099-NEC/recipient-detail.ts (the only form that resolved the year
 * from the record) so all 18 forms behave identically:
 *  - the year comes from the record (per-form year column), never hardcoded;
 *  - anything beyond the IRS intake window is clamped to the caller's
 *    ctx.irs.maxIntakeTaxYear, so enabling a new tax year is a configuration
 *    change, not a code change across 30+ generators.
 */

/**
 * IRS intake/ATS currently accepts through this year (Pub 5718 / schema table).
 *
 * EXTRACTION NOTE: this was read from the A2A_IRS_MAX_TAX_YEAR environment variable,
 * defaulting to 2025. The engine reads no environment, so 2025 is the package default and
 * every caller overrides it with ctx.irs.maxIntakeTaxYear (which buildTransmission passes
 * down to resolveIrsIntakeTaxYear for it).
 *
 * EXTRACTION NOTE: softwareIdForTaxYear below read A2A_SOFTWARE_ID_<year> and
 * A2A_SOFTWARE_ID. Those are now the `env` argument (ctx.irs.softwareIdByYear and
 * ctx.irs.softwareId).
 *
 * EXTRACTION NOTE: resolveIrsIntakeTaxYear logged a console.warn when it clamped a year.
 * The engine does not write to the console; the clamp is silent and the caller can compare
 * BuildResult.taxYear with the year it asked for.
 */
export const IRS_MAX_INTAKE_TAX_YEAR = 2025;

/**
 * The tax year the IRS is currently PROCESSING, which is what makes an older return a
 * "prior year" filing (PriorYearDataInd).
 *
 * This was derived from the calendar date as (current year - 1), which held until the IRS
 * opened the next processing year. They did that on 2026-09-28: TY2025 transmissions that
 * had been accepted days earlier came back 'Manifest Prior Year Data Indicator must be "1"',
 * because 2025 had become a prior year while the calendar still said otherwise. The IRS
 * moves on their own schedule, so this is stated rather than guessed, and callers can
 * override it (ctx.irs.currentProcessingTaxYear) when they move again.
 */
export const IRS_CURRENT_PROCESSING_TAX_YEAR = 2026;

/** The software-id half of an IrsEnvironment, all this module needs. */
export interface SoftwareIdEnv {
  softwareId?: string;
  softwareIdByYear?: Record<string, string>;
}

/**
 * IRIS manifest SchemaVersionNum per tax year: what we TRANSMIT.
 *
 * The IRS release of 2026-09-24 (TY2022-TY2024 v2.1, TY2025 v3.1, TY2026 v1.2) fixes 5.0.7 in
 * every year's manifest. Before it, TY2025 fixed 2.0.3 (the IRS raised it to 5.0.3 on
 * 2026-09-28 and to 5.0.7 for ATS from 2026-10-05, rejecting the older values with "The value
 * '5.0.3' of element 'SchemaVersionNum' does not match the {value constraint} value '5.0.7'"),
 * TY2026 fixed 5.0.6, and the pre-2025 packages had no SchemaVersionNum element at all. Years
 * not listed take the latest value; a year whose constraint moves gets its own entry here.
 */
export const SCHEMA_VERSION_BY_TAX_YEAR: Record<number, string> = {
  2022: "5.0.7",
  2023: "5.0.7",
  2024: "5.0.7",
  2025: "5.0.7",
  2026: "5.0.7",
};

/**
 * What the BUNDLED schema package under schemas/TY<year> enforces (its manifest
 * SchemaVersionNum is xsd:fixed), which is not always what the IRS is enforcing today.
 *
 * When the two disagree, a document carrying the version the IRS wants fails our own
 * pre-flight check against a package that predates the change, so the validator checks it
 * against the bundled value while we transmit the value above. When the current IRS package for
 * a year is dropped into schemas/TY<year>, set that year's entry to the package's fixed value;
 * while it equals the transmitted value, the substitution does nothing.
 */
export const BUNDLED_SCHEMA_VERSION_BY_TAX_YEAR: Record<number, string> = {
  // schemas/TY2025: IRS TY2025 v3.1 (release 2026-09-24).
  2025: "5.0.7",
  // schemas/TY2026: IRS TY2026 v1.2 (release 2026-09-24).
  2026: "5.0.7",
};

/** SchemaVersionNum for a tax year; falls back to the latest known version. */
export function schemaVersionForTaxYear(taxYear: string | number): string {
  const y = Number(taxYear);
  if (SCHEMA_VERSION_BY_TAX_YEAR[y]) return SCHEMA_VERSION_BY_TAX_YEAR[y];
  const known = Object.keys(SCHEMA_VERSION_BY_TAX_YEAR).map(Number);
  const latest = Math.max(...known);
  return SCHEMA_VERSION_BY_TAX_YEAR[latest];
}

/** Per-form year columns, in precedence order (1099s/3921/3922, 1098s, 1042-S, generic). */
const YEAR_FIELDS = ["1099_YEAR", "1098_YEAR", "TAX_YEAR", "1042_YEAR", "YEAR"] as const;

/** The record's own tax year (uncapped); falls back to the intake max.
 *  Accepts any per-form record shape (typed interfaces have no index signature). */
export function resolveTaxYear(record: unknown): string {
  const r = record as Record<string, unknown> | undefined | null;
  if (r) {
    for (const f of YEAR_FIELDS) {
      const v = r[f];
      if (v != null && String(v).trim() !== "") return String(v).trim();
    }
  }
  return String(IRS_MAX_INTAKE_TAX_YEAR);
}

/**
 * Tax year written into all IRIS XML TaxYr elements (manifest, header, form).
 * Clamped to the IRS intake window so a not-yet-accepted year files under the max.
 */
export function resolveIrsIntakeTaxYear(
  record: unknown,
  maxIntakeTaxYear: number = IRS_MAX_INTAKE_TAX_YEAR
): string {
  const cap = Number.isFinite(Number(maxIntakeTaxYear))
    ? Number(maxIntakeTaxYear)
    : IRS_MAX_INTAKE_TAX_YEAR;
  const raw = resolveTaxYear(record);
  const year = Number(raw);
  if (Number.isFinite(year) && year > cap) {
    return String(cap);
  }
  return raw;
}

/**
 * IRS-assigned Software ID for a tax year. The IRS issues a NEW Software ID per
 * year (e.g. 25M0021705 for TY2025 production, 26M0025772 for TY2026 test) —
 * supply them as ctx.irs.softwareIdByYear; ctx.irs.softwareId stays the fallback.
 * When only the fallback exists, the year prefix is rewritten to match the tax
 * year (last-resort legacy behavior — a fabricated ID the IRS never assigned,
 * so per-year env values should always be preferred).
 */
export function softwareIdForTaxYear(
  taxYear: string | number,
  env?: SoftwareIdEnv
): string {
  const y = String(taxYear).trim();
  const perYear = env?.softwareIdByYear?.[y];
  if (perYear && perYear.trim()) return perYear.trim().slice(0, 10);
  const base = (env?.softwareId || "").trim();
  if (!base) return "";
  const suffix = y.slice(-2);
  const id = base.startsWith(suffix) ? base : suffix + base.substring(2);
  return id.slice(0, 10);
}

/**
 * Force every record's tax-year field to the source file's processing year.
 *
 * The per-year form tables are the authority on which year a file belongs to
 * (form1099NEC_2025 IS tax year 2025), but individual rows can carry a stale
 * year in their own column — UAT data has 2023/2024/2026 values sitting in the
 * 2025 tables. resolveIrsIntakeTaxYear reads that column, so those files were
 * transmitted as prior-year data (TaxYr 2023) and rejected by the IRS.
 */
export function applyProcessingYearToRecords<T>(
  records: T[],
  processingYear: string | number | null | undefined
): T[] {
  const year = String(processingYear ?? "").trim();
  if (!/^\d{4}$/.test(year) || !Array.isArray(records)) return records;
  return records.map((record) => {
    const copy: any = { ...(record as any) };
    for (const field of YEAR_FIELDS) {
      if (copy[field] !== undefined && copy[field] !== null) copy[field] = year;
    }
    // Forms whose records carry no year column at all still need one.
    if (!YEAR_FIELDS.some((f) => copy[f] !== undefined && copy[f] !== null)) {
      copy.TAX_YEAR = year;
    }
    return copy as T;
  });
}
